import React, { useState } from 'react'
import { ReactComponent as IconLock } from './../../images/icons/icon_lock.svg'
import { ReactComponent as CreateSecureLink } from './../../images/secure_upload/create_secure_link_admin.svg'
import { ReactComponent as SecureLinkCreated } from './../../images/secure_upload/secure_link_created.svg'
import { ReactComponent as IconClose } from './../../images/icons/icon_close.svg'
import { useCreateSecureUploadLink } from '../../api/dealership/customers/util/useCreateSecureUploadLink'
import { AnimatePresence, motion } from 'framer-motion'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import moment from 'moment'
import toast from 'react-hot-toast'
import { Button } from '@demandlocal/components'
import { useModal } from '../useModal'
import { useCreateManualUploadSource } from '../../api/dealership/customers/useCreateManualUploadSource'

type FormValues = {
  secureLink?: string
  client_email?: string
}

const GenerateSecureLink = ({
  selectedUploadType,
}: {
  selectedUploadType:
    | 'sales'
    | 'leads'
    | 'service'
    | 'other'
    | 'hashed-customers'
    | 'opt-out-customers'
}): JSX.Element => {
  const { mutateAsync: createSecureUploadLink, isLoading } = useCreateSecureUploadLink()
  const { mutateAsync: createManualUploadSource } = useCreateManualUploadSource()
  const { closeModal } = useModal()
  const formContext = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      secureLink: undefined,
      client_email: undefined,
    },
  })

  const secureLink = useWatch({
    control: formContext.control,
    name: 'secureLink',
  })

  const {
    formState: { isSubmitting },
  } = formContext

  const [expirationDate, setExpirationDate] = useState<string | undefined>(undefined)

  //countdown to expiration date in seconds
  const [countdown, setCountdown] = useState('')
  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = moment()
      const diff = moment.duration(moment(expirationDate).diff(now))
      // format diff as HH:mm:ss
      const formatted = `${Number(diff.asHours()).toFixed(
        0,
      )}:${diff.minutes()}:${diff.seconds()}`
      setCountdown(formatted)
    }, 1000)
    return () => clearInterval(interval)
  }, [countdown, expirationDate])

  const onSubmit = (values: FormValues): void => {
    closeModal()
  }

  const createUploadLink = async () => {
    const toastId = toast.loading('Creating source...')
    const customerSourceId = await createManualUploadSource({
      data: {
        sourceName: `Secure Upload - ${selectedUploadType} - ${moment().format(
          'YYYY-MM-DD HH:mm',
        )}`,
        sourceType: 'other',
      },
    }).then((res) => res.id)
    toast.loading('Creating link...', { id: toastId })
    await createSecureUploadLink(
      {
        data: {
          selectedUploadType,
          customer_source_id: customerSourceId,
        },
      },
      {
        // @ts-ignore
        onSuccess: (data: { data: { token: string; expiration_date: string } }) => {
          formContext.setValue('secureLink', data.data?.token)
          setExpirationDate(data.data?.expiration_date)
          toast.success('Link created!', { id: toastId })
        },
      },
    )
  }

  return (
    <div className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full overflow-auto'}>
      <div className={'flex flex-row items-center justify-between gap-2'}>
        <div className={'flex'}>
          <div className={'bg-secondary-malachite-green/10 p-2 rounded-full'}>
            <IconLock className={'fill-[#107E28] stroke-[#107E28]'} />
          </div>
          <span className={'font-bold text-[28px] text-primary-xiketic'}>
            Secure Link Generator
          </span>
        </div>
        <div
          className={'font-bold cursor-pointer'}
          onClick={(e) => {
            closeModal()
          }}>
          <IconClose />
        </div>
      </div>
      <AnimatePresence initial={false} mode={'sync'}>
        <motion.div layout>
          {!secureLink && (
            <motion.div
              layout={'position'}
              className={'flex flex-col items-center mx-auto pt-20 gap-10'}>
              <motion.div layout layoutId={'secureLinkIcon'}>
                <CreateSecureLink />
              </motion.div>
              <motion.div
                layout
                initial={{
                  opacity: 0,
                  y: 100,
                }}
                exit={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: 0.5,
                }}
                className={'items-center flex flex-col gap-2'}>
                <div className={'font-bold text-primary-xiketic'}>
                  Create a secure upload link for a safe customer data upload.
                </div>
                <div className={'text-primary-xiketic'}>
                  For the best protection, send this secure link to your client.
                </div>
              </motion.div>
              <div>
                <Button
                  disabled={isLoading}
                  onClick={(e) => {
                    e.preventDefault()
                    createUploadLink()
                  }}
                  rightSlot={<IconLock className={'stroke-white fill-white'} />}>
                  Generate secure link
                </Button>
              </div>
            </motion.div>
          )}
          {secureLink && (
            <motion.div
              className={'flex flex-col items-center pt-20 gap-10'}
              layout={'position'}
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}>
              <motion.div layout layoutId={'secureLinkIcon'}>
                <SecureLinkCreated />
              </motion.div>
              <FormProvider {...formContext}>
                <motion.form
                  onSubmit={formContext.handleSubmit(onSubmit)}
                  layout={'position'}
                  initial={{
                    opacity: 0,
                    y: 100,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    delay: 0.5,
                  }}
                  className={'w-1/3 flex flex-col'}>
                  <div className={'flex justify-between mb-2'}>
                    <span className={'text-web-gray text-xs'}>Available for</span>
                    <span
                      className={
                        'text-primary-xiketic text-xl font-bold tabular-nums font-sans'
                      }>
                      {countdown}
                    </span>
                  </div>
                  <div
                    className={
                      'flex w-full items-center justify-between flex-row bg-primary-persian-blue/10 p-2 mb-8'
                    }>
                    <span className={'text-primary-persian-blue text-sm font-bold'}>
                      {secureLink}
                    </span>
                    <span
                      onClick={(e) => {
                        e.preventDefault()
                        navigator.clipboard.writeText(secureLink)
                        toast.success('Copied to clipboard')
                      }}
                      className={
                        'text-primary-persian-blue text-sm font-bold ml-3 bg-white p-2 rounded-xl cursor-pointer'
                      }>
                      Copy
                    </span>
                  </div>
                  {/*<div className={'flex mt-12 mb-4'}>
                    <TextInput
                      disabled={isSubmitting}
                      name={'client_email'}
                      label={'Client email'}
                      inverted={true}
                    />
                  </div>*/}
                  <Button type={'submit'}>Done</Button>
                </motion.form>
              </FormProvider>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
export default GenerateSecureLink
